// export default IncidentRequest;
import DocumentViewerModal from "components/DocumentViewer";
import Loading from "components/Loading";
import TabNav from "components/TabNav";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FileText } from "lucide-react";
import CivilCaseAppealRequestForm from "pages/CivilCaseAppeal/fragment/CivilCaseAppealRequestForm";
import CivilCaseRegistrarForm from "pages/CivilCaseAppeal/fragment/CivilCaseRegistrarForm";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCivilCaseComplaintById } from "services/civilCaseComplaintService";
import {
  CivilCaseComplaint,
  CivilCaseComplaintStatus,
  CivilCaseResolutionDecisionType,
  CivilCaseResolutions,
  FILE_URL,
  Roles,
} from "types";
import { DateView, errorHandler, hasRole } from "utils/helper";
import CivilCaseComplaintInformationTab from "./fragment/CivilCaseComplaintInformationTab";
import CivilCaseEvidenceTab from "./fragment/CivilCaseEvidentTab";
import CivilCaseFeedbackForm from "./fragment/CivilCaseFeedbackForm";
import CivilCaseOrgUnitForm from "./fragment/CivilCaseOrgUnitForm";
import CivilCasePowerOfAgencyTab from "./fragment/CivilCasePowerOfAgencyTab";
import CivilCaseResolutionTab from "./fragment/CivilCaseResolutionTab";
import CivilCaseWitnessTab from "./fragment/CivilCaseWitnessTab";
import CivilCaseResolutionForm from "./model/CivilCaseResolutionForm";
import ReloadButton from "components/ReloadButton";

const tab1 = ["Complaint", "Evidence Files", "Witness", "Power of Agency"];

const CivilCaseComplaintDetail: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const params = useParams();
  const [compliant, setCompliant] = useState<CivilCaseComplaint>();
  const [loading, setLoading] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);

  const [isOpenAppealRequest, setOpenAppealRequest] = React.useState(false);
  const [isOpenResolution, setOpenResolution] = React.useState(true);
  const [openEditResolution, setOpenEditResolution] = React.useState(false);
  const [selectedResolution, setSelectedResolution] =
    React.useState<CivilCaseResolutions>();

  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [isOpenRegistrar, setOpenRegistrar] = React.useState(false);

  const isClerk = hasRole(loginData?.roles, [Roles.complaintClerk]);
  const isManagement = hasRole(loginData?.roles, [Roles.management]);
  const isProsecutor = hasRole(loginData?.roles, [
    Roles.ComplaintManagementProsecutor,
  ]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.complaintRegistrar]);

  const [activeTab, setActiveTab] = useState(tab1[0]);
  const [isReadyForAppeal, setIsReadyForAppeal] = useState(false);

  const navigate = useNavigate();

  const fetchData = async (complaintId: string) => {
    setLoading(true);

    fetchCivilCaseComplaintById(complaintId)
      .then((data) => {
        setCompliant(data);
        // setIsReadyForAppeal(
        //   data?.status === "resolved" && data?.is_appeal === false,
        // );
        setIsReadyForAppeal(
          !!data?.resolutions?.some((r) => r?.registration_number) &&
            !data?.is_appeal,
        );
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (params?.complaintId) fetchData(params?.complaintId);
  }, [params]);

  useEffect(() => {
    setOpenResolution(false);
    setOpenAppealRequest(false);
  }, [activeTab]);

  // const handleStatusChange = (status: CivilCaseComplaint["status"]) => {
  //   setActionIsLoading(true);
  //   editCivilCaseComplaint({
  //     id: compliant?.id,
  //     status: status,
  //   })
  //     .then(() => {
  //       showTooltip("Successful!", "success");
  //       fetchData(params?.complaintId || "");
  //     })
  //     .catch((error) => {
  //       showTooltip(
  //         error?.response?.data?.detail || errorHandlerOnForm(error),
  //         "error",
  //       );
  //     })
  //     .finally(() => setActionIsLoading(false));
  // };

  const [appointmentFound, setAppointmentFound] = useState(false);
  const [forwardFound, setForwardFound] = useState(false);
  const [panelFound, setPanelFound] = useState(false);
  const [latestResolutionDecisionType, setLatestResolutionDecisionType] =
    useState<any>();

  useEffect(() => {
    if (compliant) {
      setAppointmentFound(
        (compliant?.resolutions ?? []).filter(
          (r) => r?.decision_type === "further_review",
        ).length > 0
          ? true
          : false,
      );
      setForwardFound(
        (compliant?.resolutions ?? []).filter(
          (r) =>
            r?.decision_type === "escalate_to_higher_authority" ||
            r?.decision_type === "forward_to_lower_authority",
        ).length > 0
          ? true
          : false,
      );
      setPanelFound(
        (compliant?.resolutions ?? []).filter(
          (r) => r?.decision_type === "on_panel",
        ).length > 0
          ? true
          : false,
      );
      const resolutions =
        compliant?.resolutions &&
        compliant?.resolutions?.sort(
          (a: any, b: any) =>
            new Date(b?.created_at).getTime() -
            new Date(a?.created_at).getTime(),
        );
      setLatestResolutionDecisionType(
        resolutions && resolutions[0] ? resolutions[0]?.decision_type : "",
      );
    }
  }, [compliant]);

  const canUpdate =
    isManagement &&
    (compliant?.resolutions?.length === 0 ||
      compliant?.status === "on_appointment");

  const lastedResolution = compliant?.resolutions?.find(
    (a) => !a?.decision_type,
  );
  const isOnlyEditResolutionForProsecutor =
    lastedResolution?.feedbacks?.sort(
      (a: any, b: any) => a?.created_at - b?.created_at,
    )[0]?.feedback_type === "respond_back";

  if (loading) return <Loading />;

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
      <div className=" mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div className="flex items-center mb-4 md:mb-0 flex-1">
              <div className="flex flex-row gap-3 items-center">
                <FileText className="w-6 h-6 md:w-16 md:h-16 text-gray-700" />
                <div>
                  <h1 className="text-xl md:text-3xl font-bold text-gray-900">
                    Complaint on Prosecutor Details
                  </h1>
                  <p className="text-gray-600 mt-1">
                    View and manage complaint on prosecutor information,
                    evidence, and resolution details
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <ReloadButton
                onReload={() => fetchData(params?.complaintId || "")}
              />
              {compliant?.status && (
                <span
                  className={`px-4 py-2 rounded-full text-sm font-bold ${
                    compliant.status === "resolved"
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : compliant.status === "pending"
                        ? "bg-amber-100 text-amber-800 border border-amber-200"
                        : compliant.status === "closed"
                          ? "bg-gray-100 text-gray-800 border border-gray-200"
                          : compliant.status === "forwarded_to_cordinator"
                            ? "bg-red-100 text-red-800 border border-red-200"
                            : compliant.status === "under_review"
                              ? "bg-purple-100 text-purple-800 border border-purple-200"
                              : compliant.status === "appealed"
                                ? "bg-red-100 text-red-800 border border-red-200"
                                : compliant.status === "assigned_to_prosecutor"
                                  ? "bg-indigo-100 text-indigo-800 border border-indigo-200"
                                  : compliant.status === "on_appointment"
                                    ? "bg-orange-100 text-orange-800 border border-orange-200"
                                    : "bg-gray-100 text-gray-800 border border-gray-200"
                  }`}
                >
                  {CivilCaseComplaintStatus[compliant.status]}
                </span>
              )}

              {compliant?.client_id && (
                <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-sm font-medium text-red-700">
                    Application Number:{" "}
                  </span>
                  <span className="font-bold text-red-900">
                    {compliant.client_id}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Complaint Info Summary */}
          {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-sm text-gray-600 font-medium">
                Client Name
              </div>
              <div className="mt-1 font-semibold text-gray-900">
                {compliant?.client_full_name || "N/A"}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-sm text-gray-600 font-medium">
                Phone Number
              </div>
              <div className="mt-1 font-semibold text-gray-900">
                {compliant?.phone_number || "N/A"}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-sm text-gray-600 font-medium">
                Date Registered
              </div>
              <div className="mt-1 font-semibold text-gray-900">
                {compliant?.complaint_reception_date
                  ? new Date(
                      compliant.complaint_reception_date,
                    ).toLocaleDateString()
                  : "N/A"}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-sm text-gray-600 font-medium">
                Department
              </div>
              <div className="mt-1 font-semibold text-gray-900">
                {compliant?.unit_name || "N/A"}
              </div>
            </div>
          </div> */}
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <button
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 mb-3 text-gray-700 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200"
              onClick={() => navigate(-1)}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
            <TabNav
              active={activeTab}
              tabs={
                [
                  ...tab1,
                  !isClerk && appointmentFound && "Appointment",
                  !isClerk && forwardFound && "Forward",
                ].filter(Boolean) as string[]
              }
              onSelect={setActiveTab}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:w-1/2">
            <div className="">
              {activeTab === "Complaint" && (
                <CivilCaseComplaintInformationTab
                  complaint={compliant}
                  setRefetch={() => fetchData(params?.complaintId || "")}
                />
              )}
              {activeTab === "Evidence Files" && compliant && (
                <CivilCaseEvidenceTab
                  compliant={compliant}
                  setRefetch={() => fetchData(params?.complaintId || "")}
                  canUpdate={canUpdate || compliant?.is_appeal ? true : false}
                />
              )}
              {activeTab === "Witness" && compliant && (
                <CivilCaseWitnessTab
                  compliant={compliant}
                  setRefetch={() => fetchData(params?.complaintId || "")}
                  canUpdate={canUpdate}
                />
              )}
              {activeTab === "Power of Agency" && compliant && (
                <CivilCasePowerOfAgencyTab
                  compliant={compliant}
                  setRefetch={() => fetchData(params?.complaintId || "")}
                  canUpdate={canUpdate}
                />
              )}
              {activeTab === "Appointment" && compliant && (
                <CivilCaseResolutionTab
                  compliant={compliant}
                  tab={"appointment"}
                />
              )}
              {activeTab === "Forward" && compliant && (
                <CivilCaseResolutionTab compliant={compliant} tab={"forward"} />
              )}
              {activeTab === "Panel" && compliant && (
                <CivilCaseResolutionTab
                  compliant={compliant}
                  tab={"on_panel"}
                />
              )}
            </div>
          </div>

          {/* Right Column - Action Panel */}
          <div className="lg:w-1/2 space-y-6">
            {/* Forward Action Card */}
            {isClerk && compliant?.status === "pending" && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-5 bg-green-600 mr-2 rounded"></span>
                  Forward
                </h3>
                <CivilCaseOrgUnitForm
                  isEdit={false}
                  compliant={compliant}
                  onClose={(refetch) => {
                    if (refetch) fetchData(params?.complaintId || "");
                  }}
                />
              </div>
            )}

            {isManagement &&
              compliant?.status === "forwarded_to_cordinator" &&
              loginData?.org_unit === compliant?.org_unit_id &&
              loginData?.admin_unit === compliant?.admin_unit_id &&
              latestResolutionDecisionType === "on_panel" && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-5 bg-green-600 mr-2 rounded"></span>
                    Forward
                  </h3>
                  <CivilCaseOrgUnitForm
                    isEdit={false}
                    compliant={compliant}
                    onClose={(refetch) => {
                      if (refetch) fetchData(params?.complaintId || "");
                    }}
                  />
                </div>
              )}

            {/* Resolution Form Card */}
            {activeTab === "Complaint" &&
              ((isManagement &&
                loginData?.org_unit === compliant?.org_unit_id &&
                loginData?.admin_unit === compliant?.admin_unit_id &&
                compliant?.status === "forwarded_to_cordinator") ||
                (isManagement &&
                  compliant?.status === "on_appointment" &&
                  compliant.resolutions?.filter(
                    (r) => r?.decision_type === "resolved",
                  ).length === 0)) && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-5 bg-green-600 mr-2 rounded"></span>
                    Resolution Decision
                  </h3>
                  <CivilCaseResolutionForm
                    isEdit={false}
                    compliant={compliant}
                    complaintId={compliant?.id || ""}
                    onClose={(refetch) => {
                      if (refetch) fetchData(params?.complaintId || "");
                    }}
                  />
                </div>
              )}

            {/* Prosecutor Resolution Form Card */}
            {activeTab === "Complaint" &&
              ((isProsecutor &&
                compliant?.status === "assigned_to_prosecutor" &&
                compliant.resolutions?.filter(
                  (r) => !r?.decision_type && !r?.s_escalated,
                ).length === 0 &&
                (lastedResolution?.feedbacks &&
                lastedResolution?.feedbacks?.length > 0
                  ? !isOnlyEditResolutionForProsecutor
                  : compliant.resolutions &&
                    compliant.resolutions?.filter((r) => !r?.decision_type)
                      .length === 0)) ||
                openEditResolution) && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-5 bg-red-600 mr-2 rounded"></span>
                    Prosecutor Resolution
                  </h3>
                  <CivilCaseResolutionForm
                    compliant={compliant}
                    complaintId={compliant?.id || ""}
                    resolution={
                      openEditResolution ? selectedResolution : undefined
                    }
                    isEdit={openEditResolution}
                    onClose={(refetch) => {
                      if (refetch) fetchData(params?.complaintId || "");
                    }}
                  />
                </div>
              )}

            {activeTab === "Complaint" &&
              isRegistrar &&
              compliant?.admin_unit_id === loginData?.admin_unit &&
              !compliant?.org_unit_id && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-5 bg-green-600 mr-2 rounded"></span>
                    Org Unit
                  </h3>
                  {/* <OrgUnitForm
                    isEdit={false}
                    compliant={compliant}
                    onClose={(refetch) => {
                      if (refetch) fetchData(params?.complaintId || "");
                    }}
                  /> */}
                </div>
              )}

            {/* Resolution Cards */}
            {activeTab === "Complaint" &&
              compliant?.resolutions &&
              compliant.resolutions
                ?.filter(
                  (r) =>
                    r?.decision_type === "resolved" ||
                    r?.decision_type === "close" ||
                    (isManagement && r.s_escalated) ||
                    (isProsecutor && !r.s_escalated),
                )
                .map((resolution, index) => {
                  const decisionType = resolution?.decision_type
                    ? CivilCaseResolutionDecisionType[resolution.decision_type]
                    : "";

                  const feedbacks = (resolution?.feedbacks || [])?.sort(
                    (a: any, b: any) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime(),
                  );
                  const latestFeedback = feedbacks && feedbacks?.[0];

                  return (
                    <div
                      key={index}
                      className="bg-white rounded-2xl shadow-lg overflow-hidden"
                    >
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`rounded-lg p-2 ${
                                decisionType === "Resolved"
                                  ? "bg-green-50 text-green-600"
                                  : "bg-red-50 text-red-600"
                              }`}
                            >
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <h4 className="font-semibold text-gray-800">
                              {decisionType || resolution.s_escalated
                                ? "Prosecutor Decision"
                                : "Decision"}
                            </h4>
                          </div>
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                            Decision #{index + 1}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="rounded-lg bg-white p-2 shadow-sm">
                                <svg
                                  className="h-5 w-5 text-red-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="font-medium text-gray-700">
                                  Decision Document
                                </p>
                                <p className="text-sm text-gray-500">
                                  Reason for {decisionType}
                                </p>
                              </div>
                            </div>
                            <DocumentViewerModal
                              documents={
                                (resolution?.uploaded_documents?.filter(
                                  (r) =>
                                    r?.document_type ===
                                    "comp_resolution_document",
                                ) || []) as any
                              }
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RESOLUTION_PROCESS}`}
                              layout="list"
                              label="Resolution Documents"
                              hideEmptyFile={true}
                            />
                            <DocumentViewerModal
                              documents={
                                (resolution?.amended_documents?.filter(
                                  (r) =>
                                    r?.document_type ===
                                    "comp_panel_decision_document",
                                ) || []) as any
                              }
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RESOLUTION_PROCESS}`}
                              layout="list"
                              label="Amended Documents"
                              hideEmptyFile={true}
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="rounded-lg bg-gray-50 p-4">
                            <p className="mb-2 text-sm font-medium text-gray-600">
                              Decision Summary
                            </p>
                            <p className="text-gray-800">
                              {resolution.decision_summary ??
                                "No summary provided"}
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="rounded-lg border border-gray-200 p-4">
                              <p className="text-sm font-medium text-gray-600">
                                {decisionType} Date
                              </p>
                              <p className="mt-1 font-semibold text-gray-800">
                                {DateView(resolution?.appointment_date)}
                              </p>
                            </div>

                            {compliant?.registration_date && (
                              <div className="rounded-lg border border-gray-200 p-4">
                                <p className="text-sm font-medium text-gray-600">
                                  Registration Date
                                </p>
                                <p className="mt-1 font-semibold text-gray-800">
                                  {DateView(compliant?.registration_date)}
                                </p>
                              </div>
                            )}
                          </div>

                          {compliant?.registration_number && (
                            <div className="rounded-lg border border-gray-200 p-4">
                              <p className="text-sm font-medium text-gray-600">
                                Registration Number
                              </p>
                              <p className="mt-1 font-semibold text-gray-800">
                                {compliant.registration_number}
                              </p>
                            </div>
                          )}

                          <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                              <svg
                                className="h-5 w-5 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">
                                Resolved By
                              </p>
                              <p className="font-semibold text-gray-800">
                                {resolution?.resolved_by ?? "Not specified"}
                              </p>
                            </div>
                          </div>

                          {/* Feedback */}
                          {feedbacks && feedbacks.length > 0 && (
                            <div className="rounded-lg border border-gray-200 p-5">
                              <div className="mb-4 flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-gray-900">
                                  Feedback History
                                  <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                    {feedbacks.length} feedback
                                    {feedbacks.length !== 1 ? "s" : ""}
                                  </span>
                                </h4>
                                <div className="flex items-center text-sm text-gray-500">
                                  <svg
                                    className="mr-1 h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  Newest first
                                </div>
                              </div>

                              <div className="space-y-4">
                                {feedbacks.map((feedback, feedbackIndex) => (
                                  <div
                                    key={feedback.id}
                                    className={`rounded-lg border p-4 transition-all duration-200 ${
                                      feedbackIndex === 0
                                        ? "border-blue-300 bg-blue-50"
                                        : "border-gray-200 bg-white"
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-center gap-3">
                                        <div
                                          className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                            feedbackIndex === 0
                                              ? "bg-blue-100 text-blue-600"
                                              : "bg-gray-100 text-gray-600"
                                          }`}
                                        >
                                          <svg
                                            className="h-4 w-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                            />
                                          </svg>
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-gray-900">
                                            Feedback #
                                            {feedbacks.length - feedbackIndex}
                                            {feedbackIndex === 0 && (
                                              <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                                Latest
                                              </span>
                                            )}
                                          </p>
                                          <p className="mt-1 text-sm text-gray-600">
                                            {feedback.summary}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="flex flex-col items-end">
                                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                          {feedback.feedback_type ===
                                          "respond_back"
                                            ? "Respond Back"
                                            : "Approved"}
                                        </span>
                                        <span className="mt-1 text-xs text-gray-500">
                                          {DateView(feedback.created_at)}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                                      <div className="flex items-center gap-2">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100">
                                          <svg
                                            className="h-3 w-3 text-purple-600"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </div>
                                        <span className="text-xs text-gray-600">
                                          By:{" "}
                                          <span className="font-medium">
                                            {feedback.given_by_role}
                                          </span>
                                        </span>
                                      </div>
                                      {isProsecutor &&
                                        feedbackIndex === 0 &&
                                        compliant?.admin_unit_id ===
                                          loginData?.admin_unit &&
                                        compliant?.org_unit_id ===
                                          loginData?.org_unit &&
                                        isOnlyEditResolutionForProsecutor && (
                                          <button
                                            className="text-sm font-medium text-red-600 border border-red-600 px-3 py-1 rounded hover:bg-red-600 hover:text-white transition"
                                            onClick={() => {
                                              setSelectedResolution(resolution);
                                              setOpenEditResolution(true);
                                            }}
                                          >
                                            Edit Resolution
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isRegistrar &&
                            compliant?.status === "forwarded_to_registrar" &&
                            !resolution?.registration_date &&
                            compliant?.admin_unit_id ===
                              loginData?.admin_unit && (
                              <button
                                className="w-full rounded-lg bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 text-white transition-all duration-200 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                onClick={() => setOpenRegistrar(true)}
                              >
                                <div className="flex items-center justify-center">
                                  <svg
                                    className="mr-2 h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  Add Registration
                                </div>
                              </button>
                            )}

                          {/* Action Buttons */}
                          <div className="space-y-3 pt-4">
                            {isManagement &&
                              // !resolution?.s_escalated &&
                              loginData?.admin_unit ===
                                compliant?.admin_unit_id &&
                              loginData?.org_unit === compliant?.org_unit_id &&
                              // isReadyForAppeal &&
                              compliant?.registration_number &&
                              (resolution?.decision_type === "resolved" ||
                                resolution?.decision_type === "close") && (
                                <button
                                  className="w-full rounded-lg bg-gradient-to-r from-rose-600 to-rose-700 px-4 py-3 text-white transition-all duration-200 hover:from-rose-700 hover:to-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2"
                                  onClick={() => setOpenAppealRequest(true)}
                                >
                                  {actionIsLoading ? (
                                    <div className="flex items-center justify-center">
                                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                      <span className="ml-2">Loading...</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center">
                                      <svg
                                        className="mr-2 h-5 w-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                        />
                                      </svg>
                                      Request Appeal
                                    </div>
                                  )}
                                </button>
                              )}
                            {isManagement &&
                              resolution?.s_escalated &&
                              (!latestFeedback ||
                                latestFeedback?.feedback_type !==
                                  "respond_back" ||
                                (latestFeedback?.created_at &&
                                  resolution?.updated_at &&
                                  latestFeedback?.created_at <
                                    resolution?.updated_at)) && (
                                <div className="">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <span className="w-2 h-5 bg-green-600 mr-2 rounded"></span>
                                    Feedback
                                  </h3>
                                  <CivilCaseFeedbackForm
                                    compliant={compliant}
                                    resolution={resolution}
                                    onClose={(refetch) => {
                                      if (refetch)
                                        fetchData(params?.complaintId || "");
                                    }}
                                  />
                                </div>
                              )}
                          </div>
                        </div>
                        {isOpenRegistrar && (
                          <CivilCaseRegistrarForm
                            resolution={resolution}
                            onClose={(reload) => {
                              setOpenRegistrar(false);
                              if (reload) {
                                fetchData(params?.complaintId || "");
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>

        {isOpenAppealRequest && compliant && (
          <CivilCaseAppealRequestForm
            complaintId={compliant?.id || ""}
            compliant={compliant}
            onClose={(navChange) => {
              if (navChange) navigate("/civil-appeal");
              else setOpenAppealRequest(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CivilCaseComplaintDetail;
